import * as React from 'react';
import { action, computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import get from 'lodash/get';
import Icon from './Icon';
import DownloadControls from './Document/DownloadControls';
import {
    DataStore,
    EntityGroupStore,
    AccessibilityStore,
    ScreenSizeStore,
    FilterStore,
} from '../stores';
import { ShareClass } from '../models/entity';
import Translate from './Translate';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import getQueryResults from '../utils/query/getQueryResults';
import {
    FundGroupHeaderElement as IFundGroupHeaderElement,
    FundGroupHeaderWatchlist,
} from '../models/configuration/FundGroupHeader';
import HTag from './HTag';
import WatchListToggle from './WatchListToggle';
import { camelCase } from 'change-case';
import { SubGroupedFund } from '../models/groupedFunds';
import { FundGroupHeader as FundGroupHeaderModel } from '../models/configuration/FundGroupHeader';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../models/configuration/explorer/DownloadControls';
import {
    ChildReferenceComponent,
    ChildReferenceProps,
} from '../utils/accessibility/ChildReferenceComponent';
import { ChildReferences } from '../utils/accessibility/ChildReferences';
import {
    KeyboardEvents,
    EventDirection,
} from '../utils/accessibility/KeyboardEvents';
import { KEYBOARD_KEYS } from '../constants';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

const FundHeader = styled<any, any>('div', 'fundHeader', 'fundGroupHeader')`
    ${propStyles};
    top: ${p => `${p.stickyOffset || 0}px`};
`;

const FundGroupHeaderWatchlist = styled<any, any>(
    'div',
    'fundGroupHeaderWatchlist',
    'fundGroupHeader'
)`
    ${propStyles}
`;

const Controls = styled<any, any>('div', 'controls', 'fundGroupHeader')`
    ${propStyles}
    display: flex;
    flex-direction: row;
`;

FundHeader.defaultProps = {
    position: ['sticky', 'static'],
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    fontFamily: 'heading',
    fontWeight: 'medium',
    fontSize: 'md',
    paddingX: 'md',
    paddingY: 'md',
    color: 'white',
    bgColor: 'secondary',
    borderWidth: '1px',
    borderColor: 'white',
    borderStyle: 'none none solid none',
};

const AdditionalElement = styled<any, any>(
    'div',
    'additionalElement',
    'fundGroupHeader'
)`
    ${propStyles};
`;

AdditionalElement.defaultProps = {
    marginX: 'sm',
    marginY: '2px',
};

const Label = styled<any, any>('label', 'label', 'fundGroupHeader')`
    ${propStyles};
`;

const LabelValue = styled<any, any>('span', 'labelValue', 'fundGroupHeader')`
    ${propStyles};
`;

const iconRightStyleProps = {
    order: 1,
    margin: '0 0 0 auto',
};

const ExpandButton = styled<any, any>(
    'button',
    'expandButton',
    'fundGroupHeader'
)`
    ${propStyles};
`;

ExpandButton.defaultProps = {
    margin: '0',
    padding: '0',
    bgColor: 'transparent',
    borderStyle: 'none',
};

interface FundGroupHeaderProps<THtmlInstance>
    extends ChildReferenceProps<THtmlInstance> {
    theme?: { [key: string]: React.CSSProperties };
    dataStore?: DataStore;
    entityGroupStore?: EntityGroupStore;
    accessibilityStore?: AccessibilityStore;
    screenSizeStore?: ScreenSizeStore;
    shareClasses: Array<ShareClass>;
    fundName: string;
    parentFundName?: string;
    isSubFund: boolean;
    metricContext?: string;
    subGroupedFund?: SubGroupedFund;
    fundGroupChildReferences: ChildReferences<HTMLDivElement>;
    fundGroupIndex: number;
    metricId: string;
    instanceListener?: () => void;
    filterStore?: FilterStore;
}

@inject(
    'dataStore',
    'entityGroupStore',
    'accessibilityStore',
    'screenSizeStore',
    'filterStore'
)
@observer
class FundGroupHeader extends ChildReferenceComponent<
    FundGroupHeaderProps<HTMLDivElement>,
    HTMLDivElement
> {
    keyboardEvents: KeyboardEvents<HTMLElement>;

    constructor(props: FundGroupHeaderProps<HTMLDivElement>) {
        super(props);
        this.initializeKeyboardEvents();
    }

    focusFundGroupHeader = (index: number, direction: EventDirection) => {
        const { fundGroupChildReferences } = this.props;
        if (fundGroupChildReferences.childReferences[index]) {
            fundGroupChildReferences.focusChild(index);
            return;
        }

        let nextIndex = 0;
        if (direction === 'up') {
            nextIndex = fundGroupChildReferences.getPreviousChildIndex(index);
        }
        if (direction === 'down') {
            nextIndex = fundGroupChildReferences.getNextChildIndex(index);
        }
        this.focusFundGroupHeader(nextIndex, direction);
    };

    initializeKeyboardEvents() {
        this.keyboardEvents = new KeyboardEvents({
            name: 'FundGroup',
            debug: false,
            defaults: {
                navigation: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
                accept: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
            },
        });

        // KEY EVENTS
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.DOWN_ARROW,
            'down',
            () => {
                const { fundGroupIndex, fundGroupChildReferences } = this.props;
                const index =
                    (fundGroupIndex + 1) %
                    fundGroupChildReferences.childReferences.length;
                this.focusFundGroupHeader(index, 'down');
            }
        );

        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.UP_ARROW, 'down', () => {
            const { fundGroupIndex, fundGroupChildReferences } = this.props;
            const index =
                fundGroupIndex === 0
                    ? fundGroupChildReferences.childReferences.length - 1
                    : fundGroupIndex - 1;
            this.focusFundGroupHeader(index, 'up');
        });

        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.HOME, 'down', () => {
            this.focusFundGroupHeader(0, 'up');
        });

        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.END, 'down', () => {
            const { fundGroupChildReferences } = this.props;
            const index =
                fundGroupChildReferences.childReferences.filter(
                    ref => ref !== undefined
                ).length - 1;
            this.focusFundGroupHeader(index, 'down');
        });

        this.keyboardEvents.bindEventToMany(
            [
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.ENTER },
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.SPACE },
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.SPACEBAR },
            ],
            () => {
                const { entityGroupStore } = this.props;
                if (this.headerExpandable) {
                    entityGroupStore.toggleExpanded();
                }
            }
        );
    }

    get shouldApplyFocus() {
        return false;
    }

    @computed
    get expander() {
        return this.props.dataStore.config.fundGroupHeader.expander || {};
    }

    @computed
    get tile() {
        return this.props.dataStore.config.fundGroupHeader.tile;
    }

    @computed
    get additionalElementsQueries(): Array<IFundGroupHeaderElement> {
        const { isSubFund, dataStore } = this.props;
        const {
            additionalElements,
            subHeaderAdditionalElements,
        } = dataStore.config.fundGroupHeader;
        return isSubFund ? subHeaderAdditionalElements : additionalElements;
    }

    @computed
    get headerExpandIconFilename() {
        const {
            expandIconFileName = 'HeaderChevronIconExpand',
            collapseIconFileName = 'HeaderChevronIconCollapse',
        } = this.expander;

        return this.props.entityGroupStore.expanded
            ? collapseIconFileName
            : expandIconFileName;
    }

    @computed
    get headerExpandable() {
        const {
            expandable = true,
        } = this.props.dataStore.config.fundGroupHeader;

        return expandable;
    }

    @computed
    get showExpanderIcon() {
        const { showIcon = true } = this.expander;

        return showIcon;
    }

    @computed
    get firstShareClass(): ShareClass {
        const { entityGroupStore } = this.props;
        const [firstShareClass] = entityGroupStore.shareClasses;
        return firstShareClass;
    }

    @computed
    get tileIconFilename() {
        const { property, propertyFilenameMap } = this.tile;
        const propertyValue = get(
            this.firstShareClass,
            `properties_pub.${property}.value`,
            ''
        );
        return propertyFilenameMap[propertyValue] || '';
    }

    @computed
    get hasDocumentDownloads(): boolean {
        return this.props.entityGroupStore.rowType === 'Documents';
    }

    @computed
    get isSubFund(): boolean {
        return !!this.props.isSubFund;
    }

    @computed
    get watchlistEnabled(): boolean {
        const groupWatchlist = this.groupWatchlist;
        return !!(this.isSubFund
            ? this.props.subGroupedFund &&
              this.props.subGroupedFund.valid &&
              groupWatchlist.enabled
            : groupWatchlist.enabled);
    }

    @computed
    get groupWatchlist(): FundGroupHeaderWatchlist {
        const { watchlist } = this.fundGroupHeader;
        if (watchlist) {
            return (
                (this.isSubFund
                    ? watchlist.fundSubGroup
                    : watchlist.fundGroup) || {}
            );
        }
        return {};
    }

    @computed
    get fundHeader() {
        const { theme, isSubFund } = this.props;
        return {
            ...(isSubFund ? theme.SubFundHeader : theme.FundHeader),
        };
    }

    @computed
    get fundHeaderAdditionalElementTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderFundName
            ...theme.FundHeaderFundName,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElement
                : theme.FundHeaderAdditionalElement),
        };
    }

    @computed
    get fundHeaderAdditionalElementLabelTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderLabel
            ...theme.FundHeaderLabel,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElementLabel
                : theme.FundHeaderAdditionalElementLabel),
        };
    }

    @computed
    get fundHeaderAdditionalElementValueTheme() {
        const { theme, isSubFund } = this.props;
        return {
            // backwards compatibility for theme.FundHeaderValue
            ...theme.FundHeaderValue,
            ...(isSubFund
                ? theme.SubFundHeaderAdditionalElementValue
                : theme.FundHeaderAdditionalElementValue),
        };
    }

    @computed
    get fundHeaderExpandIconTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderExpandIcon = theme.FundHeaderExpandIcon;
        if (isSubFund) {
            fundHeaderExpandIcon = {
                ...fundHeaderExpandIcon,
                ...theme.SubFundHeaderExpandIcon,
            };
        }
        return fundHeaderExpandIcon;
    }

    @computed
    get fundHeaderTileIconTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderTileIcon = theme.FundHeaderTileIcon;
        if (isSubFund) {
            fundHeaderTileIcon = {
                ...fundHeaderTileIcon,
                ...theme.SubFundHeaderTileIcon,
            };
        }
        return fundHeaderTileIcon;
    }

    @computed
    get stickyConfig() {
        const { isSubFund, screenSizeStore, dataStore } = this.props;
        const { stickyConfig } = screenSizeStore;

        // For backwards compatibility, use the sticky offset in the Fund Group header that is defined for mobile;
        if (dataStore.config.stickyConfig) {
            if (stickyConfig) {
                const config = isSubFund
                    ? stickyConfig.subGroup
                    : stickyConfig.group;
                return config;
            }
        } else if (screenSizeStore.breakpointSize === 'sm') {
            return {
                enabled: true,
                offset: this.props.dataStore?.config?.fundGroupHeader
                    ?.stickyOffset,
            };
        }
    }

    @computed
    get moduleExpandedHeader() {
        const { stickyConfig } = this.props.screenSizeStore;
        return stickyConfig?.moduleExpandedHeader;
    }

    @computed
    get stickyOffset() {
        const { isFiltersOpen } = this.props.filterStore;
        const {
            filterConfig,
        } = this.props.dataStore?.config.selectedModuleConfig;
        let offset = this.stickyConfig?.offset;
        if (
            filterConfig?.top &&
            isFiltersOpen &&
            this.moduleExpandedHeader &&
            this.moduleExpandedHeader.enabled &&
            this.moduleExpandedHeader.offset &&
            !isNullOrUndefined(offset)
        ) {
            offset = offset + this.moduleExpandedHeader.offset;
        }
        return offset;
    }

    @computed
    get stickyPosition() {
        if (this.stickyConfig) {
            return this.stickyConfig?.enabled ? 'sticky' : 'static';
        }

        return 'static';
    }

    @computed
    get additionalElements() {
        return (
            this.additionalElementsQueries &&
            this.additionalElementsQueries.map((elementQuery, idx) => {
                const { label, value } = this.additionalElementsQueryResults(
                    elementQuery
                );
                const ariaId = `fund-${this.headerTextId}-group-header-add-ele-${idx}`;
                return (
                    value && (
                        <AdditionalElement
                            key={`${label}${idx}`}
                            {...this.fundHeaderAdditionalElementTheme}
                        >
                            <Label
                                {...this.fundHeaderAdditionalElementLabelTheme}
                                htmlFor={ariaId}
                            >
                                <Translate>{label}</Translate>
                            </Label>
                            <LabelValue
                                {...this.fundHeaderAdditionalElementValueTheme}
                                id={ariaId}
                            >
                                <Translate>{value}</Translate>
                            </LabelValue>
                        </AdditionalElement>
                    )
                );
            })
        );
    }

    additionalElementsQueryResults(query) {
        const { dataStore } = this.props;
        const { culture } = dataStore;
        const { entityType } = dataStore.config;
        const queryContext: IQueryContext = {
            entityByType: {
                [entityType]: this.firstShareClass,
            },
        };

        const translateFn =
            dataStore.stores.translation &&
            dataStore.stores.translation.translate;

        return getQueryResults(
            queryContext,
            query,
            culture,
            dataStore.appDataProps,
            null,
            null,
            translateFn
        );
    }

    @computed
    get headerText() {
        const { dataStore, fundName } = this.props;
        let headerText = fundName;
        const fundGroupHeader = dataStore.config.fundGroupHeader;
        if (fundGroupHeader) {
            const {
                fundGroupPropertyValueAliases: valueAliases,
                fundGroupPropertyGlobalValueAliases: globalValueAliases,
            } = fundGroupHeader;
            if (valueAliases || globalValueAliases) {
                const aliases = dataStore.getAliases(
                    valueAliases,
                    globalValueAliases
                );
                headerText = get(aliases, fundName, fundName);
            }
        }
        return headerText;
    }

    @computed
    get headerTextThemeName() {
        const { isSubFund } = this.props;
        return isSubFund ? 'SubFundHeaderFundName' : 'FundHeaderFundName';
    }

    @computed
    get headerTextId() {
        return this.headerText.toLowerCase().replace(/\W/g, '-');
    }

    @computed
    get property(): string {
        return this.isSubFund
            ? this.fundGroupHeader.fundSubGroupProperty
            : this.fundGroupHeader.fundGroupProperty;
    }

    @computed
    get parentProperty(): string {
        return this.fundGroupHeader.fundGroupProperty;
    }

    @computed
    get clientCodeProperty(): string {
        const { dataStore } = this.props;
        return (
            (dataStore &&
                dataStore.config &&
                dataStore.config.explorer &&
                dataStore.config.explorer.watchlist &&
                dataStore.config.explorer.watchlist.property) ||
            'isin'
        );
    }

    @computed
    get fundGroupHeader(): FundGroupHeaderModel {
        const { dataStore } = this.props;
        const fundGroupHeader =
            (dataStore &&
                dataStore.config &&
                dataStore.config.fundGroupHeader) ||
            {};
        return fundGroupHeader;
    }

    @computed
    get childEntities() {
        const { dataStore, fundName, subGroupedFund } = this.props;
        const { watchlistHierarchy } = dataStore;
        let childEntities = [];
        const value = fundName;
        const parentValue = subGroupedFund && subGroupedFund.parent;
        if (this.isSubFund) {
            childEntities =
                (watchlistHierarchy[parentValue] &&
                    watchlistHierarchy[parentValue][value]) ||
                [];
        } else {
            childEntities =
                watchlistHierarchy[value]?._all ||
                (Array.isArray(watchlistHierarchy[value]) &&
                    watchlistHierarchy[value]) ||
                [];
        }
        return childEntities;
    }

    @computed
    get inWatchList() {
        const { dataStore, subGroupedFund } = this.props;
        if (!subGroupedFund || subGroupedFund.valid) {
            const { watchList, watchlistHierarchy } = dataStore;
            if (!watchlistHierarchy) {
                return false;
            }
            const childEntities = this.childEntities;
            let allPresent = false;
            if (childEntities.length > 0) {
                allPresent = true;
                for (const entity of childEntities) {
                    const watchlistPropertyValue = get(
                        entity,
                        `properties_pub.${dataStore.config.watchListProperty}.value`,
                        ''
                    );
                    if (!watchList.includes(watchlistPropertyValue)) {
                        allPresent = false;
                        break;
                    }
                }
            }
            return allPresent;
        }
        return false;
    }

    @action
    toggleWatchList = () => {
        if (this.inWatchList) {
            this.removeWatchList();
        } else {
            this.addWatchList();
        }
    };

    @action
    addWatchList = () => {
        const { dataStore } = this.props;
        const updatedWatchList = toJS(this.props.dataStore.watchList);
        const childEntities = this.childEntities;
        if (childEntities && childEntities.length > 0) {
            const childWatchlistValues = childEntities
                .map(childEntity => {
                    return get(
                        childEntity,
                        `properties_pub.${dataStore.config.watchListProperty}.value`,
                        ''
                    );
                })
                .filter(watchlistValue => {
                    return !updatedWatchList.includes(watchlistValue);
                });
            updatedWatchList.push(...childWatchlistValues);
            this.props.dataStore.watchList = updatedWatchList;
            dataStore.analyticsHelper.logEvent({
                event: 'watchlist_add',
                eventType: 'click',
                context: {
                    isin: childWatchlistValues,
                },
            });
        }
    };

    @action
    removeWatchList = () => {
        const { dataStore } = this.props;
        const updatedWatchList = toJS(this.props.dataStore.watchList);
        const childEntities = this.childEntities;
        if (childEntities && childEntities.length > 0) {
            const childWatchlistValues = childEntities.map(childEntity => {
                return get(
                    childEntity,
                    `properties_pub.${dataStore.config.watchListProperty}.value`,
                    ''
                );
            });
            this.props.dataStore.watchList = updatedWatchList.filter(
                value => !childWatchlistValues.includes(value)
            );
            dataStore.analyticsHelper.logEvent({
                event: 'watchlist_remove',
                eventType: 'click',
                context: {
                    isin: childWatchlistValues,
                },
            });
        }
    };

    @action
    onClickFundHeader = () => {
        const { entityGroupStore } = this.props;
        if (this.headerExpandable) {
            entityGroupStore.toggleExpanded();
        }
    };

    @computed
    get watchListToggleTheme() {
        const { theme } = this.props;
        return this.isSubFund
            ? theme.SubFundHeaderWatchListToggle
            : theme.FundHeaderWatchListToggle;
    }

    @computed
    get fundHeaderExpandButtonTheme() {
        const { theme, isSubFund } = this.props;
        let fundHeaderExpandButton = theme.FundHeaderExpandButton;
        if (isSubFund) {
            fundHeaderExpandButton = {
                ...fundHeaderExpandButton,
                ...theme.SubFundHeaderExpandButton,
            };
        }
        return fundHeaderExpandButton;
    }

    @computed
    get watchlistIcons() {
        return this.groupWatchlist.icons || {};
    }

    getFundGroupPropertyId(): string | number | Array<string | number> {
        const { dataStore } = this.props;
        const { fundGroupPropertyId, fundGroupProperty } = dataStore.config;
        const fundIdentifier = fundGroupPropertyId || fundGroupProperty;
        const fundIdentifierValue = this.firstShareClass?.properties_pub[
            fundIdentifier
        ]?.value;
        return !fundGroupPropertyId && typeof fundIdentifierValue === 'string'
            ? camelCase(fundIdentifierValue)
            : fundIdentifierValue;
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        return this.props.dataStore?.config?.groupHeaderDownloadControlActions;
    }

    @computed
    get headerTextLevel() {
        const { fundGroupHeader } = this.props.dataStore?.config || {};
        if (this.isSubFund) {
            return fundGroupHeader?.fundSubGroupHeadingLevel || 3;
        }
        return fundGroupHeader?.fundGroupHeadingLevel || 2;
    }

    render() {
        const {
            dataStore,
            entityGroupStore,
            accessibilityStore,
            isSubFund,
            metricId,
        } = this.props;

        const labelId = `${metricId}-label`;

        return (
            <FundHeader
                id={metricId}
                aria-label={this.headerText}
                ref={this.getReference}
                onClick={this.onClickFundHeader}
                cursor={this.headerExpandable ? 'pointer' : 'auto'}
                data-metric-id={metricId}
                tabIndex={0}
                stickyOffset={this.stickyOffset}
                position={this.stickyPosition}
                zIndex={this.isSubFund ? 11 : 12}
                {...this.fundHeader}
                {...this.keyboardEvents.onKeyProps}
            >
                <Controls
                    {...(this.expander.align === 'right' &&
                        iconRightStyleProps)}
                >
                    {this.watchlistEnabled && (
                        <FundGroupHeaderWatchlist>
                            <WatchListToggle
                                onClick={e => {
                                    e.stopPropagation();
                                    this.toggleWatchList();
                                }}
                                active={this.inWatchList}
                                describedby={labelId}
                                metricContext={metricId}
                                iconOnFileName={this.watchlistIcons.on}
                                iconOffFileName={this.watchlistIcons.off}
                                {...this.watchListToggleTheme}
                            />
                        </FundGroupHeaderWatchlist>
                    )}
                    {this.headerExpandable && this.showExpanderIcon && (
                        <ExpandButton
                            aria-label={accessibilityStore.buttonAccessibilityValue(
                                'FundHeaderExpandButton',
                                `Expand group toggle for {label}`,
                                {
                                    label: this.headerText,
                                }
                            )}
                            aria-expanded={entityGroupStore.expanded}
                            onKeyDown={this.keyboardEvents.onKeyProps.onKeyDown}
                            {...this.fundHeaderExpandButtonTheme}
                        >
                            <Icon
                                aria-hidden="true"
                                fileName={this.headerExpandIconFilename}
                                iconSize="md"
                                {...this.fundHeaderExpandIconTheme}
                            />
                        </ExpandButton>
                    )}
                </Controls>
                {this.tile && (
                    <Icon
                        fileName={this.tileIconFilename}
                        {...(this.tile.align === 'right' &&
                            iconRightStyleProps)}
                        iconSize="md"
                        {...this.fundHeaderTileIconTheme}
                    />
                )}
                <HTag
                    id={labelId}
                    componentTheme={this.headerTextThemeName}
                    defaultStyleProps={{
                        marginX: 'sm',
                        marginY: '0',
                        fontFamily: 'heading',
                        fontWeight: 'medium',
                        fontSize: 'md',
                    }}
                    level={this.headerTextLevel}
                    text={this.headerText}
                />
                {this.additionalElements}
                {this.hasDocumentDownloads && (
                    <DownloadControls
                        label="Download"
                        margin="0 0 0 auto"
                        hidePreview={dataStore.isZipDocument(
                            entityGroupStore.allSelectedDownloads
                        )}
                        count={entityGroupStore.downloadCount}
                        disabled={entityGroupStore.downloadCount === 0}
                        onClick={e => {
                            e.stopPropagation();
                            dataStore.downloadDocuments(
                                entityGroupStore.allSelectedDownloads,
                                this.firstShareClass
                            );
                        }}
                        onPreviewClick={(
                            e,
                            elementOptions: PreviewActionOptions
                        ) => {
                            e.stopPropagation();
                            dataStore.previewDocuments(
                                entityGroupStore.allSelectedDownloads,
                                this.firstShareClass,
                                elementOptions
                            );
                        }}
                        href={dataStore.getDocumentLink(
                            entityGroupStore.allSelectedDownloads,
                            this.firstShareClass
                        )}
                        downloadButtonFileName="HeaderDownloadIcon"
                        componentThemeKey={
                            isSubFund ? 'SubFundHeader' : 'FundHeader'
                        }
                        fileName={dataStore.getFileName(
                            entityGroupStore.allSelectedDownloads,
                            this.firstShareClass
                        )}
                        metricContext={metricId}
                        clientCode={this.getFundGroupPropertyId()}
                        document={entityGroupStore.allSelectedDownloads}
                        documentDisplayName={this.headerText}
                        actions={this.downloadControlActions}
                        disableExtendedInfo={true}
                        describedBy={labelId}
                    />
                )}
            </FundHeader>
        );
    }
}

export default withTheme(FundGroupHeader);



// WEBPACK FOOTER //
// ./src/Components/FundGroupHeader.tsx